import React, { useLayoutEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Table as AntTable } from 'antd';
import { useDealsTable } from './deals-table.utils';
import { RemoveThreadModal } from './components/removeThreadModal/RemoveThreadModal';
import { DealsTableFooter } from './components/footer/DealsTableFooter';
import { EmptyTableList } from './components/emptyList/EmptyDealsList';
import { DealsTableSkeleton } from './components/skeleton/DealsTableSkeleton';
import {
  EngagementSubrow,
  EngagementTableRow,
} from '../../../store/deals/deals.types';
import { ExpandRowChevron } from './assets/ExpandRowChevron';
import { RenameAccountModal } from './components/renameAccountModal/RenameAccountModal';
import { NoDataEngagements } from './components/noData/NoDataEngagements';
import { ExtensionNIPopup } from '../../../components/ExtensonNIPopup/ExtensionNIPopup';
import { SampleDataLabel } from './components/sampleDataLabel/SampleDataLabel';

export const DealsTable: React.FC = (): JSX.Element => {
  const {
    user,
    cols,
    rowSelection,
    selectedRows,
    subrowSelection,
    dealsList,
    deleteModalOpen,
    toggleDeleteModal,
    closeModalAfterSuccess,
    removeDeal,
    renameModalOpen,
    toggleRenameModal,
    currentDeal,
    currentDealsQty,
    totalDealsQty,
    currentPageNumber,
    loadPreviousPage,
    loadNextPage,
    loading,
    dealsNoData,
    createColumnsForExpandedRow,
  } = useDealsTable();

  const [tableScroll, setTableScroll] = useState(window.innerWidth <= 1366);

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const [open, setOpen] = useState(true);

  const history = useHistory();

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const setTableOverflow = () => {
      setTableScroll(window.innerWidth <= 1366);
    };

    window.addEventListener('resize', setTableOverflow);

    return () => window.removeEventListener('resize', setTableOverflow);
  }, []);

  const isOneSelectedDeal = () => {
    let result = false;
    if (currentDeal) {
      result = true;
    } else if (selectedRows?.length) {
      if (selectedRows.length > 1) {
        result = false;
      } else result = true;
    }
    return result;
  };

  const onRowClick = (row: EngagementTableRow) => {
    if (
      row?.engagementId &&
      user?.userId &&
      row.subrows &&
      row.subrows.length <= 1
    ) {
      const { userId, orgId } = user;
      history.push(
        `/orgs/${orgId}/users/${userId}/engagements/${row.engagementId}/threads/${row.branchId}`
      );
    }
  };

  const onSubrowClick = (subrow: EngagementSubrow) => {
    if (subrow?.engagementId && subrow?.engagementId && user?.userId) {
      const { userId, orgId } = user;
      history.push(
        `/orgs/${orgId}/users/${userId}/engagements/${subrow.engagementId}/threads/${subrow.branchId}`
      );
    }
  };

  const onExpandRow = (expanded: boolean, record: EngagementTableRow): void => {
    const keys = expandedKeys;

    const currExpandedRowsKeys = expanded
      ? keys.concat(record.key)
      : keys.filter((key) => key !== record.key);

    setExpandedKeys(currExpandedRowsKeys);
  };

  const isOneSelected = isOneSelectedDeal();

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
  };

  const conditionalPageBody = dealsNoData ? (
    // <NoDataFirstTime />
    <NoDataEngagements />
  ) : (
    <div className="deals-table" ref={tableWrapperRef} onClick={toggle}>
      <SampleDataLabel />
      <RemoveThreadModal
        open={deleteModalOpen}
        removeDeal={removeDeal}
        onClose={toggleDeleteModal}
        closeAfterGetStatus={closeModalAfterSuccess}
        isOnlyOneDeal={isOneSelected}
      />
      <RenameAccountModal open={renameModalOpen} onClose={toggleRenameModal} />
      <AntTable
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={cols}
        dataSource={dealsList}
        loading={false}
        pagination={false}
        scroll={tableScroll ? { y: 440 } : { y: '70vh' }}
        tableLayout="fixed"
        rowClassName={(record: EngagementTableRow) =>
          `${expandedKeys.includes(record.key) ? 'expanded-row' : ''}`
        }
        expandRowByClick
        locale={{
          emptyText: <EmptyTableList />,
        }}
        onRow={(row) => ({
          onClick: () => {
            onRowClick(row);
          },
        })}
        expandable={{
          rowExpandable: (row) =>
            (row?.subrows && row.subrows.length > 1) || false,

          onExpand: (expanded: boolean, record: EngagementTableRow) =>
            onExpandRow(expanded, record),

          expandIcon: ({ expanded, onExpand, record }) => (
            <ExpandRowChevron
              isVisible={(record.subrows && record.subrows.length > 1) || false}
              open={expanded}
              onExpand={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
                e.stopPropagation();
                onExpand(record, e);
              }}
            />
          ),

          expandedRowRender: (row) => (
            <div className="deals-table__expanded-row">
              {row?.subrows && row.subrows.length > 1 ? (
                <AntTable
                  rowSelection={{ type: 'checkbox', ...subrowSelection }}
                  dataSource={row.subrows}
                  columns={createColumnsForExpandedRow(row.subrows)}
                  loading={false}
                  pagination={false}
                  tableLayout="fixed"
                  onRow={(subrow) => ({
                    onClick: () => {
                      onSubrowClick(subrow);
                    },
                  })}
                  expandable={{
                    rowExpandable: () => false,
                    expandedRowRender: () => null,
                    expandIcon: ({ expanded, onExpand, record }) => (
                      <ExpandRowChevron
                        isVisible={record.contacts.length > 1}
                        open={expanded}
                        onExpand={(
                          e: React.MouseEvent<HTMLElement, MouseEvent>
                        ) => {
                          e.stopPropagation();
                          onExpand(record, e);
                        }}
                        isInSubrow
                      />
                    ),
                  }}
                />
              ) : null}
            </div>
          ),
        }}
      />

      <DealsTableFooter
        selectedRowsLength={selectedRows.length || 0}
        totalDealsQty={totalDealsQty}
        currentDealsQty={currentDealsQty}
        currentPageNumber={currentPageNumber}
        currentListLength={dealsList.length || 0}
        loadPreviousPage={loadPreviousPage}
        loadNextPage={loadNextPage}
        openDeleteModal={toggleDeleteModal}
      />
    </div>
  );

  return (
    <DealsTableSkeleton isLoading={loading}>
      <>
        <ExtensionNIPopup />
        {conditionalPageBody}
      </>
    </DealsTableSkeleton>
  );
};
